import {
  Box,
  Card,
  CardContent,
  Chip,
  Grid,
  IconButton,
  MenuItem,
  Typography,
  Menu,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  CircularProgress,
} from "@mui/material";
import { format } from "date-fns/format";
import { differenceInHours } from "date-fns/differenceInHours";
import { isValid } from "date-fns/isValid";
import { parseISO } from "date-fns/parseISO";
import {
  Event as EventIcon,
  AccessTime as TimeIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  CalendarToday as CalendarIcon,
  Print as PrintIcon,
} from "@mui/icons-material";
import { MoreVertical as MoreVerticalIcon } from "lucide-react";
import { useState } from "react";
import { isPastBooking } from "./BookingSection";
import RescheduleBookingForm from "./RescheduleBookingForm";
import { updateBooking } from "../../apis/bookingApi";
import theme from "../theme/Theme";
import { BookingsByUser } from "../../schemas/bookings";
import { toast } from "react-toastify";

const formatBookingDate = (time: string): string => {
  const bookingDate = new Date(time);
  return bookingDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
};

const formatBookingTime = (
  startInput: string | Date,
  endInput: string | Date
): string => {
  const start =
    typeof startInput === "string" ? parseISO(startInput) : startInput;
  const end = typeof endInput === "string" ? parseISO(endInput) : endInput;

  if (!isValid(start) || !isValid(end)) {
    return "Invalid time";
  }

  return `${format(start, "h:mm a")} - ${format(end, "h:mm a")}`;
};

// Check if booking is within 24 hours
const isWithin24Hours = (bookingDate: Date): boolean => {
  const now = new Date();
  const hoursDifference = differenceInHours(bookingDate, now);
  return hoursDifference <= 24;
};

// Receipt options dialog component
const ReceiptOptionsDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  receiptUrl: string;
}> = ({ open, onClose, receiptUrl }) => {
  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Receipt Options</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Choose how you would like to access your receipt:
        </DialogContentText>
        
        <Box sx={{ display: 'flex', flexDirection: 'column', mt: 2, gap: 2 }}>
          <Button 
            variant="contained" 
            startIcon={<PrintIcon />}
            onClick={() => {
              if (receiptUrl === null) {
                toast.error("Receipt not available as this booking was created by one of our employee.", {
                  position: "top-center",
                  autoClose: 5000,
                  style: {
                    width: "400px",
                  }
                  
                })
                return;
              }
              const printWindow = window.open(receiptUrl, '_blank');
              if (printWindow) {
                printWindow.onload = () => {
                  printWindow.print();
                };
              }
              onClose();
            }}
          >
            Open & Print Receipt
          </Button>
          
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Tip: When viewing the receipt in your browser, you can save it as a PDF using 
            your browser's print function (Ctrl+P or Cmd+P) and selecting "Save as PDF" 
            as the destination.
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const BookingItem: React.FC<{
  booking: BookingsByUser;
  onCancelBooking?: (id: string) => void;
}> = ({ booking, onCancelBooking }) => {
  const isPast = isPastBooking(booking);
  const [menuOpen, setMenuOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [receiptOptionsOpen, setReceiptOptionsOpen] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    setMenuOpen(true);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuOpen(false);
  };

  const handleRescheduleOption = () => {
    handleMenuClose();
    setEditDialogOpen(true);
  };

  const handleCancelClick = () => {
    handleMenuClose();
    setCancelDialogOpen(true);
  };
  
  const handleReceiptOptions = () => {
    handleMenuClose();
    setReceiptOptionsOpen(true);
  };

  const handleConfirmCancel = async () => {
    setIsCancelling(true);
    try {
      if (onCancelBooking) {
        await onCancelBooking(booking.bookingId); // Await if async
      }
      setCancelDialogOpen(false);
    } catch (error) {
      // Optionally show an error toast/message here
    } finally {
      setIsCancelling(false);
    }
  };

  const within24Hours = isWithin24Hours(new Date(booking.startTime));
  const isBookingCancellable = !isPast && booking.statusHistoryData.bookingStatus !== "cancelled";

  return (
    <>
    {booking.statusHistoryData.bookingStatus !== "temporary" && (
    <Card
      elevation={2}
      sx={{
        marginBottom: 2,
        opacity: isPast || booking.statusHistoryData.bookingStatus === "cancelled" ? 0.7 : 1,
        backgroundColor:
          isPast || booking.statusHistoryData.bookingStatus === "cancelled"
            ? theme.palette.grey[100]
            : theme.palette.background.paper,
        transition: "all 0.3s ease",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: 3,
        },
      }}
    >
      <CardContent>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={10} sm={7} md={8}>
            <Box display="flex" alignItems="center">
              <EventIcon
                fontSize="small"
                sx={{ mr: 1, color: "text.secondary" }}
              />
              <Typography variant="h6" sx={{ fontWeight: "bold", mb: 0.5 }}>
                {booking.laneOrCage.name}
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" mt={1}>
              <CalendarIcon
                fontSize="small"
                sx={{ mr: 1, color: "text.secondary" }}
              />
              <Typography variant="body2" color="text.secondary">
                {formatBookingDate(booking.startTime)}
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" mt={0.5}>
              <TimeIcon
                fontSize="small"
                sx={{ mr: 1, color: "text.secondary" }}
              />
              <Typography variant="body2" color="text.secondary">
                {formatBookingTime(booking.startTime, booking.endTime)}
              </Typography>
            </Box>
          </Grid>
          <Grid
            item
            xs={12}
            sm={4}
            md={3}
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
            }}
          >
            <Chip
              icon={
                booking.statusHistoryData.bookingStatus === "confirmed" ? (
                  <CheckCircleIcon />
                ) : booking.statusHistoryData.bookingStatus === "cancelled" ? (
                  <CancelIcon />
                ) : (
                  <EventIcon />
                )
              }
              label={
                booking.statusHistoryData.bookingStatus.charAt(0).toUpperCase() +
                booking.statusHistoryData.bookingStatus.slice(1)
              }
              color={
                booking.statusHistoryData.bookingStatus === "confirmed"
                  ? "primary"
                  : booking.statusHistoryData.bookingStatus === "cancelled"
                  ? "error"
                  : "default"
              }
              variant={isPast ? "outlined" : "filled"}
            />
            <Typography variant="subtitle1" sx={{ ml: 2, fontWeight: "bold" }}>
              ${booking.payment.totalAmount.toFixed(2)}
            </Typography>
            <IconButton
              onClick={handleMenuOpen}
              sx={{ ml: 2, color: "text.secondary" }}
              disabled={!isBookingCancellable}
              size="small"
            >
              <MoreVerticalIcon />
            </IconButton>

            {/* Menu */}
            <Menu
              anchorEl={anchorEl}
              open={menuOpen}
              onClose={handleMenuClose}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "left",
              }}
              sx={{
                minWidth: 200,
              }}
            >
              {booking.statusHistoryData.bookingStatus !== "rescheduled" && (
                <MenuItem onClick={handleRescheduleOption}>Reschedule</MenuItem>
              )}

              <MenuItem
                sx={{ color: theme.palette.warning.main }}
                onClick={handleReceiptOptions}
              >
                Receipt Options
              </MenuItem>

              <MenuItem
                onClick={handleCancelClick}
                sx={{ color: theme.palette.error.main }}
              >
                Cancel booking
              </MenuItem>
            </Menu>

            {/* Receipt options dialog */}
            <ReceiptOptionsDialog
              open={receiptOptionsOpen}
              onClose={() => setReceiptOptionsOpen(false)}
              receiptUrl={booking.payment.receiptUrl}
            />

            {/* Reschedule booking dialog */}
                {booking.statusHistoryData.bookingStatus !== "rescheduled" && (
              <Dialog
                open={editDialogOpen}
                onClose={() => setEditDialogOpen(false)}
                aria-labelledby="edit-dialog-title"
                aria-describedby="edit-dialog-description"
              >
                <DialogTitle id="edit-dialog-title">
                  Reschedule Booking
                </DialogTitle>
                <DialogContent>
                  <DialogContentText id="edit-dialog-description">
                    You can reschedule this booking for a different date and
                    time.
                  </DialogContentText>
                  <RescheduleBookingForm
                    booking={booking}
                    open={editDialogOpen}
                    onClose={() => setEditDialogOpen(false)}
                    onReschedule={
                      async (
                        newStartTime,
                        newEndTime,
                        selectedDate,
                        bookingStatus,
                        email
                      ) =>
                      await updateBooking(
                        booking.id,
                        bookingStatus,
                        email,
                        booking.laneOrCage.name,
                        booking.userDetails.firstName,
                        booking.userDetails.lastName,
                        booking.payment.totalHours,
                        booking.payment.totalAmount,
                        selectedDate,
                        newStartTime,
                        newEndTime
                      )
                    }
                  />
                </DialogContent>
                <DialogActions>
                  <Button
                    onClick={() => setEditDialogOpen(false)}
                    color="primary"
                  >
                    Cancel
                  </Button>
                </DialogActions>
              </Dialog>
            )}

            {/* Cancel booking confirmation dialog */}
            <Dialog
              open={cancelDialogOpen}
              onClose={() => !isCancelling && setCancelDialogOpen(false)}
              aria-labelledby="cancel-dialog-title"
              aria-describedby="cancel-dialog-description"
            >
              <DialogTitle id="cancel-dialog-title">
                Confirm Cancellation
              </DialogTitle>
              <DialogContent>
                <DialogContentText id="cancel-dialog-description">
                  Are you sure you want to cancel this booking for{" "}
                  {formatBookingDate(booking.startTime)} at{" "}
                  {/* {format(booking.startTime, "h:mm a")}? */}
                  {isValid(new Date(booking.startTime))
                    ? format(new Date(booking.startTime), "h:mm a")
                    : "Invalid Time"}
                  ?
                  {within24Hours ? (
                    <Box
                      component="span"
                      sx={{
                        display: "block",
                        mt: 2,
                        color: theme.palette.error.main,
                        fontWeight: "bold",
                      }}
                    >
                      This booking is within 24 hours of the start time. You
                      will not be eligible for a refund according to our
                      cancellation policy.
                    </Box>
                  ) : (
                    <Box
                      component="span"
                      sx={{
                        display: "block",
                        mt: 2,
                        color: theme.palette.success.main,
                      }}
                    >
                      This booking is more than 24 hours from the start time.
                      You will receive a full refund if you cancel now.
                    </Box>
                  )}
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={() => setCancelDialogOpen(false)}
                  color="primary"
                  disabled={isCancelling}
                >
                  Keep Booking
                </Button>
                <Button
                  onClick={handleConfirmCancel}
                  color="error"
                  variant="contained"
                  sx={{ ml: 1 }}
                  disabled={isCancelling}
                >
                  {isCancelling ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    "Cancel Booking"
                  )}
                </Button>
              </DialogActions>
            </Dialog>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
    )
    }
    </>
  );
};

export default BookingItem;